/* jshint ignore:start */
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";
import V1CashfreeCheckout from "../../Payment/V1CashfreeCheckout";

const BENEFIT_LABEL = {
  teleconsult: "Doctor teleconsults",
  home_visit: "Doctor home visits",
  nursing: "Nursing visits",
  physio: "Physiotherapy sessions",
};

export default function SubscriptionPlans() {
  const { ApiBaseUrl, setIsLoginShow } = useContext(MyContext);
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [cycle, setCycle] = useState("annual");
  const [loading, setLoading] = useState(true);
  const [onlinePlan, setOnlinePlan] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    const uid = secureLocalStorage.getItem("Userid");
    axios
      .post(`${ApiBaseUrl}/subscription/plans`, { id: uid || undefined })
      .then((r) => {
        setPlans(r.data.plans || []);
        setCurrent(r.data.current || null);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const subscribe = (plan) => {
    const uid = secureLocalStorage.getItem("Userid");
    if (!uid) { setIsLoginShow(true); return; }
    setOnlinePlan(plan);
  };

  const createOnlineCheckout = async () => {
    const uid = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ApiBaseUrl}/subscription/checkout`, {
      id: uid,
      plan_id: onlinePlan.id,
      billing_cycle: cycle,
    });
    if (!res.data.Result) throw new Error(res.data.message || "Online checkout could not start");
    return {
      payment_session_id: res.data.payment_session_id,
      cashfree_order_id: res.data.order_id,
      ref: res.data.order_id,
      environment: res.data.environment,
    };
  };

  const confirmOnline = async ({ cashfree_order_id }) => {
    const uid = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ApiBaseUrl}/subscription/subscribe`, {
      id: uid,
      plan_id: onlinePlan.id,
      billing_cycle: cycle,
      transaction_id: cashfree_order_id,
    });
    if (!res.data.Result) throw new Error(res.data.message || "Confirmation failed");
    return res.data;
  };

  const onlineSuccess = () => {
    setOnlinePlan(null);
    showToast("success", "Plan activated");
    load();
  };

  const cancel = () => {
    const uid = secureLocalStorage.getItem("Userid");
    if (!uid) { setIsLoginShow(true); return; }
    if (!window.confirm("Cancel your RapidCare plan? You will keep access until it is cancelled.")) return;
    axios
      .post(`${ApiBaseUrl}/subscription/cancel`, { id: uid })
      .then((r) => {
        showToast(r.data.Result ? "success" : "error", r.data.message);
        load();
      })
      .catch(() => showToast("error", "Something went wrong."));
  };

  const priceFor = (p) => (cycle === "annual" ? p.annual_price : p.price);
  // The server refuses a cycle a plan has no price for, so don't offer it.
  const sellable = plans.filter((p) => Number(priceFor(p)) > 0);

  return (
    <div className="width-container py-[40px]">
      <div className="text-center max-w-[720px] mx-auto">
        <p className="text-[13px] uppercase tracking-wide font-[700] text-rm-primary">RapidCare Membership</p>
        <h1 className="text-[34px] leading-tight font-[800] text-rm-ink mt-[6px]">
          Health cover that actually gets used
        </h1>
        <p className="text-rm-muted mt-[10px]">
          Included consults every month, a standing discount on diagnostics and priority dispatch when
          you need someone at home. Cancel any time.
        </p>
      </div>

      <div className="flex justify-center mt-[26px]">
        <div className="inline-flex border border-rm-line rounded-[14px] p-[4px] bg-white">
          {[
            { key: "monthly", label: "Monthly" },
            { key: "annual", label: "Annual · 2 months free" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setCycle(opt.key)}
              className={`px-[18px] py-[9px] rounded-[11px] text-[14px] font-[600] transition ${
                cycle === opt.key ? "bg-rm-gradient text-white" : "text-rm-muted"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {current && (
        <div className="mt-[28px] border border-rm-primary rounded-[18px] p-[20px] bg-white shadow-rm-soft">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[13px] uppercase tracking-wide text-rm-primary font-[700]">Your plan</p>
              <h3 className="text-[22px] font-[800] text-rm-ink">{current.plan_name}</h3>
              <p className="text-rm-muted text-[14px]">
                Renews {current.renewal_date ? String(current.renewal_date).slice(0, 10) : "-"}
              </p>
            </div>
            <button type="button" className="inline-block border border-rm-line text-rm-ink font-[700] text-[14px] px-[16px] py-[10px] rounded-[12px] bg-white hover:border-rm-primary hover:text-rm-primary transition" onClick={cancel}>Cancel plan</button>
          </div>
          {current.benefits && current.benefits.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-[16px]">
              {current.benefits.map((b) => (
                <div key={b.kind} className="border border-rm-line rounded-[14px] p-[14px]">
                  <p className="text-[13px] text-rm-muted">{BENEFIT_LABEL[b.kind] || b.label}</p>
                  <p className="text-[20px] font-[800] text-rm-ink">
                    {b.remaining}<span className="text-[14px] font-[500] text-rm-muted"> / {b.included} left</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-center text-rm-muted py-[50px]">Loading plans…</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-[20px] mt-[30px]">
          {sellable.map((p, idx) => {
            const isCurrent = current && current.plan_id === p.id;
            const featured = idx === 1 && sellable.length > 2;
            return (
              <div
                key={p.id}
                className={`rounded-[20px] p-[24px] bg-white border ${
                  featured ? "border-rm-primary shadow-rm" : "border-rm-line shadow-rm-soft"
                }`}
              >
                {featured && (
                  <span className="inline-block bg-rm-gradient text-white text-[12px] font-[700] px-[10px] py-[4px] rounded-[8px] mb-[10px]">
                    Most popular
                  </span>
                )}
                <h3 className="text-[22px] font-[800] text-rm-ink">{p.name}</h3>
                <p className="mt-[8px]">
                  <span className="text-[32px] font-[800] text-rm-ink">₹{priceFor(p)}</span>
                  <span className="text-rm-muted text-[14px]"> / {cycle === "annual" ? "year" : "month"}</span>
                </p>

                <ul className="mt-[16px] space-y-[8px] text-[14px] text-rm-ink">
                  {p.teleconsult_sessions > 0 && <li>✓ {p.teleconsult_sessions} teleconsults included every month</li>}
                  {p.home_visit_sessions > 0 && <li>✓ {p.home_visit_sessions} doctor home visits every month</li>}
                  {p.nursing_sessions > 0 && <li>✓ {p.nursing_sessions} nursing visits every month</li>}
                  {p.physio_sessions > 0 && <li>✓ {p.physio_sessions} physiotherapy sessions every month</li>}
                  {p.discount_percent > 0 && <li>✓ {p.discount_percent}% off every diagnostic test</li>}
                  {p.wallet_credit > 0 && <li>✓ ₹{p.wallet_credit} wallet credit each month</li>}
                  {p.priority_minutes && <li>✓ Priority dispatch within {p.priority_minutes} minutes</li>}
                  {p.family_limit > 1 && <li>✓ Covers {p.family_limit} family members</li>}
                </ul>

                {p.features && p.features.length > 0 && (
                  <p className="text-[13px] text-rm-muted mt-[14px]">{p.features.join(" · ")}</p>
                )}

                <button
                  type="button"
                  disabled={isCurrent}
                  onClick={() => subscribe(p)}
                  className={`w-full mt-[20px] py-[11px] rounded-[12px] font-[700] text-[15px] ${
                    isCurrent
                      ? "bg-rm-softbg text-rm-muted cursor-not-allowed"
                      : "bg-rm-gradient text-white"
                  }`}
                >
                  {isCurrent ? "Your current plan" : "Pay online & activate"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !sellable.length && (
        <p className="text-center text-rm-muted py-[50px]">
          {plans.length
            ? `No plans are available on ${cycle} billing right now.`
            : "No plans are available right now."}
        </p>
      )}

      <div className="mt-[36px] border border-rm-line rounded-[18px] p-[22px] bg-rm-softbg">
        <h4 className="font-[800] text-rm-ink text-[18px]">Looking after a team?</h4>
        <p className="text-rm-muted mt-[6px]">
          Corporate wellness plans start at ₹299 per employee per month for teams of 20 or more.
        </p>
        <button type="button" className="inline-block bg-rm-gradient text-white font-[700] text-[14px] px-[16px] py-[10px] rounded-[12px] mt-[12px]" onClick={() => navigate("/corporate")}>
          Talk to us about corporate cover
        </button>
      </div>

      <p className="text-[13px] text-rm-muted mt-[20px] text-center">
        Online payments open a secure Cashfree checkout and your plan activates automatically.
      </p>

      {onlinePlan && (
        <div className="mt-[24px]">
          <V1CashfreeCheckout
            amount={priceFor(onlinePlan)}
            createCheckout={createOnlineCheckout}
            confirmPayment={confirmOnline}
            onSuccess={onlineSuccess}
            onCancel={() => setOnlinePlan(null)}
          />
        </div>
      )}
    </div>
  );
}
